import { useCallback, useState } from 'react';
import { getTokenFromFirebase } from '../firebase/hooks';
import { getNestConfig } from '../utilities/config';
import {
  useGetFileUrlFromStorageMutation,
  useGetSignUrlFromStorageForRequesterLazyQuery,
} from 'graphql/generated/hasura';
import { useAuth } from '../auth/context/AuthContext';
import { STORE } from 'hooks/useUploadFileApi';

export const useGetFileFromStorage = () => {
  const [getFileUrlFromStorage] = useGetFileUrlFromStorageMutation({});
  const [getFileUrlFromStorageForRequester] =
    useGetSignUrlFromStorageForRequesterLazyQuery({});
  const [loading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const { nestApi } = getNestConfig();
  const { selectedAccount } = useAuth();
  const fetchFileFromStorage = async (
    fileId: string,
    patientId?: string,
  ): Promise<string> => {
    try {
      setIsLoading(true);
      const token = await getTokenFromFirebase();
      const headers = {
        Accept: 'application/pdf',
        Authorization: `Bearer ${token}`,
        ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
      };
      const fileUrl = patientId
        ? `${nestApi}${STORE}/file?fileId=${fileId}&patientId=${patientId}`
        : `${nestApi}${STORE}/file?fileId=${fileId}`;

      const options = {
        method: 'GET',
        headers: headers,
        credentials: 'include' as const,
      };

      const res = await fetch(fileUrl, options);

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setIsLoading(false);
      setHasError(false);
      return blobUrl;
    } catch (error) {
      setIsLoading(false);
      setHasError(true);
      console.error(`There was an error fetching file, ${error}`);
      return '';
    }
  };

  const fetchVonageFileFromStorage = async (
    fileId: string,
    appointmentId: string,
  ): Promise<string> => {
    try {
      setIsLoading(true);
      const token = await getTokenFromFirebase();
      const headers = {
        Authorization: `Bearer ${token}`,
        ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
      };
      const fileUrl = `${nestApi}${STORE}/vonage-signedUrl?fileId=${fileId}&appointmentId=${appointmentId}`;

      const options = {
        method: 'GET',
        headers: headers,
      };
      const res = await (await fetch(fileUrl, options)).json();

      setIsLoading(false);
      setHasError(false);
      return res.url;
    } catch (error) {
      setIsLoading(false);
      setHasError(true);
      console.error(`There was an error fetching file, ${error}`);
      return '';
    }
  };

  const getUrlFileFromVonageStorage = async (
    fileId: string,
    appointmentId: string,
  ): Promise<string> => {
    try {
      setIsLoading(true);
      const token = await getTokenFromFirebase();
      const headers = {
        Accept: 'application/pdf',
        Authorization: `Bearer ${token}`,
        ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
      };
      const fileUrl = `${nestApi}${STORE}/vonage-signedUrl?fileId=${fileId}&appointmentId=${appointmentId}`;

      const options = {
        method: 'GET',
        headers: headers,
      };

      const res = await fetch(fileUrl, options);
      const response = await res.json();
      return response.url;
    } catch (error) {
      console.error(`There was an error fetching file, ${error}`);
      return '';
    } finally {
      setIsLoading(false);
      setHasError(true);
    }
  };

  const getUrlFileFromEducationCourseStorage = useCallback(
    async (fileId: string, courseId: string): Promise<string> => {
      try {
        setIsLoading(true);
        const token = await getTokenFromFirebase();
        const headers = {
          Authorization: `Bearer ${token}`,
          ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
        };
        const fileUrl = `${nestApi}${STORE}/education?fileId=${fileId}&courseId=${courseId}`;

        const options = {
          method: 'GET',
          headers: headers,
        };

        const res = await fetch(fileUrl, options);
        const response = await res.json();
        return response.url;
      } catch (error) {
        console.error(`There was an error fetching file, ${error}`);
        return '';
      } finally {
        setIsLoading(false);
        setHasError(true);
      }
    },
    [nestApi, selectedAccount],
  );

  const getUrlFileFromProductsStorage = async (
    fileId: string,
  ): Promise<string> => {
    try {
      setIsLoading(true);
      const token = await getTokenFromFirebase();
      const headers = {
        Authorization: `Bearer ${token}`,
        ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
      };
      const fileUrl = `${nestApi}${STORE}/products?fileId=${fileId}`;

      const options = {
        method: 'GET',
        headers: headers,
      };

      const res = await fetch(fileUrl, options);
      const response = await res.json();
      return response.url;
    } catch (error) {
      console.error(`There was an error fetching file, ${error}`);
      return '';
    } finally {
      setIsLoading(false);
      setHasError(true);
    }
  };

  const getProxyUrlFileByUrl = async (url: string): Promise<string> => {
    try {
      setIsLoading(true);
      const token = await getTokenFromFirebase();
      const headers = {
        Authorization: `Bearer ${token}`,
        ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
      };
      const fileUrl = url;

      const options = {
        method: 'GET',
        headers: headers,
      };

      const res = await fetch(fileUrl, options);
      const response = await res.json();
      return response.url;
    } catch (error) {
      console.error(`There was an error fetching file, ${error}`);
      return '';
    } finally {
      setIsLoading(false);
      setHasError(true);
    }
  };

  const fetchHormoneInstructionsFromStorage = async (
    testType: string,
  ): Promise<string> => {
    try {
      setIsLoading(true);
      const token = await getTokenFromFirebase();
      const headers = {
        Accept: 'application/pdf',
        Authorization: `Bearer ${token}`,
        ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
      };

      const fileUrl = `${nestApi}${STORE}/instructions/${testType}`;

      const options = {
        method: 'GET',
        headers: headers,
      };

      const res = await fetch(fileUrl, options);

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setIsLoading(false);
      setHasError(false);
      return blobUrl;
    } catch (error) {
      setIsLoading(false);
      setHasError(true);
      console.error(`There was an error fetching file, ${error}`);
      return '';
    }
  };

  const fetchImage = async (fileId: string): Promise<string> => {
    try {
      setIsLoading(true);
      const image = await getFileUrlFromStorage({
        variables: {
          fileId,
        },
      });
      setIsLoading(false);
      setHasError(false);

      return image.data?.GetSignUrlFormStorage?.url || '';
    } catch (error) {
      setIsLoading(false);
      setHasError(true);
      console.error(`There was an error fetching file, ${error}`);
      return '';
    }
  };

  const fetchPatientImage = async (
    fileId: string,
    patientId: string,
  ): Promise<string> => {
    try {
      setIsLoading(true);
      const image = await getFileUrlFromStorageForRequester({
        variables: {
          fileId,
          requestedUserId: patientId,
        },
      });
      setIsLoading(false);
      setHasError(false);

      return image.data?.GetSignUrlFromStorageForRequester?.url || '';
    } catch (error) {
      setIsLoading(false);
      setHasError(true);
      console.error(`There was an error fetching file, ${error}`);
      return '';
    }
  };

  const fetchPDFFileByLink = async (url: string) => {
    try {
      setIsLoading(true);
      const token = await getTokenFromFirebase();
      const headers = {
        Accept: 'application/pdf',
        Authorization: `Bearer ${token}`,
        ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
      };
      const options = {
        method: 'GET',
        headers: headers,
      };
      const res = await fetch(url, options);

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setIsLoading(false);
      setHasError(false);
      return blobUrl;
    } catch (error) {
      setIsLoading(false);
      setHasError(true);
      console.error(`There was an error fetching file, ${error}`);
      return '';
    }
  };

  const downloadURI = (uri: string, name = 'file') => {
    const link = document.createElement('a');
    link.download = name;
    link.href = uri;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileByUrl = useCallback(
    async (url: string) => {
      const token = await getTokenFromFirebase();
      const headers = {
        Authorization: `Bearer ${token}`,
        ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
      };
      const options = {
        method: 'GET',
        headers: headers,
      };
      try {
        const response = await fetch(url, options);
        if (!response.ok) throw new Error('Network response was not ok');

        const blob = await response.blob();
        return new File([blob], 'file');
      } catch (error) {
        console.error('Download failed:', error);
      }
    },
    [selectedAccount],
  );

  const downloadFileFromUrl = async (url: string, name = 'document.pdf') => {
    const token = await getTokenFromFirebase();
    const headers = {
      Authorization: `Bearer ${token}`,
      ...(selectedAccount && { 'x-sub-patient-id': selectedAccount }),
    };
    const options = {
      method: 'GET',
      headers: headers,
    };
    try {
      const response = await fetch(url, options);

      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = name;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  };

  return {
    loading,
    hasError,
    fetchFileFromStorage,
    fetchImage,
    getFileByUrl,
    downloadURI,
    downloadFileFromUrl,
    fetchPDFFileByLink,
    fetchPatientImage,
    fetchHormoneInstructionsFromStorage,
    getProxyUrlFileByUrl,
    fetchVonageFileFromStorage,
    getUrlFileFromVonageStorage,
    getUrlFileFromProductsStorage,
    getUrlFileFromEducationCourseStorage,
  };
};
